import { useEffect, useState } from "react";
import { api, brl } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, LineChart, Line, CartesianGrid } from "recharts";
import { TrendingUp, Wallet, Receipt } from "lucide-react";

const MONTHS_PT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
function fmtMonth(ym) {
  const [y, m] = ym.split("-");
  return `${MONTHS_PT[Number(m) - 1]}/${y.slice(2)}`;
}

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid} className="bg-[#FFFFFF] border border-stone-200 rounded-lg p-6 flex flex-col justify-between min-h-[140px]">
      <div className="w-10 h-10 rounded-md bg-[#EAF0EA] flex items-center justify-center text-[#083A6B]">
        <Icon size={18} strokeWidth={1.6} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">{label}</div>
        <div className="font-serif text-4xl text-stone-900 leading-none">{value}</div>
      </div>
    </div>
  );
}

const TerraTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-[#FFFFFF] border border-stone-200 rounded-md px-3 py-2 shadow-sm text-xs">
      <div className="text-stone-500 mb-1">{label}</div>
      <div className="font-medium text-[#083A6B]">{formatter ? formatter(payload[0].value) : payload[0].value}</div>
    </div>
  );
};

export default function Relatorios() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/financial").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const byPousada = (data?.revenue_by_pousada || []).map((r) => ({ name: r.pousada, valor: r.valor }));
  const byMonth = (data?.revenue_by_month || []).map((r) => ({ name: fmtMonth(r.month), valor: r.valor }));

  return (
    <div>
      <PageHeader
        eyebrow="Financeiro"
        title="Relatórios"
        description="Acompanhe receita por pousada e por mês em toda a rede."
      />

      <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
        <Stat testid="stat-receita-total" icon={Wallet} label="Receita total" value={brl(data?.total ?? 0)} />
        <Stat testid="stat-total-reservas" icon={Receipt} label="Reservas" value={data?.reservas_count ?? 0} />
        <Stat testid="stat-ticket-medio" icon={TrendingUp} label="Ticket médio" value={brl(data?.ticket_medio ?? 0)} />
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-[#FFFFFF] border border-stone-200 rounded-lg p-6">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Receita por pousada</div>
          {byPousada.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-stone-500">Nenhuma reserva registrada.</div>
          ) : (
            <div className="h-72" data-testid="chart-pousada">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byPousada} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEEBE7" vertical={false} />
                  <XAxis dataKey="name" angle={-20} textAnchor="end" interval={0} tick={{ fontSize: 11, fill: "#57534E" }} height={60} />
                  <YAxis tick={{ fontSize: 11, fill: "#57534E" }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<TerraTooltip formatter={(v) => brl(v)} />} />
                  <Bar dataKey="valor" fill="#083A6B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-[#FFFFFF] border border-stone-200 rounded-lg p-6">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-4">Evolução mensal</div>
          {byMonth.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-sm text-stone-500">Sem dados mensais.</div>
          ) : (
            <div className="h-72" data-testid="chart-mensal">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={byMonth} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEEBE7" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#57534E" }} />
                  <YAxis tick={{ fontSize: 11, fill: "#57534E" }} tickFormatter={(v) => `R$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<TerraTooltip formatter={(v) => brl(v)} />} />
                  <Line type="monotone" dataKey="valor" stroke="#D4A652" strokeWidth={2.5} dot={{ r: 4, fill: "#D4A652" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
